/**
 * Client Repository (Refactored)
 *
 * This is a facade that provides backward compatibility while delegating
 * to specialized modules:
 * - ClientQueryRepository: Read operations
 * - ClientCommandRepository: Write operations
 * - ClientWorkflowService: Business logic & workflows
 *
 * This refactoring improves maintainability by separating concerns and
 * reducing file size from 471 lines to ~180 lines per module.
 */

import { BaseRepository, RepositoryResult, PaginatedResult, QueryOptions, PaginationParams } from './BaseRepository';
import { Client } from '@/shared/types';

// Import specialized modules
import { clientQueryRepository } from './ClientQueryRepository';
import { clientCommandRepository } from './ClientCommandRepository';
import { clientWorkflowService } from './ClientWorkflowService';
import type { ClientDependencies } from './ClientWorkflowService';

/**
 * Client Repository (Facade)
 *
 * Provides a unified interface to client operations while delegating
 * to specialized modules. This maintains backward compatibility with
 * existing code while improving code organization.
 *
 * @deprecated Prefer using the specialized modules directly:
 * - Use clientQueryRepository for read operations
 * - Use clientCommandRepository for write operations
 * - Use clientWorkflowService for business logic
 */
export class ClientRepository extends BaseRepository<Client> {
  protected tableName = 'clients' as const;

  // ============================================================================
  // QUERY OPERATIONS (delegated to ClientQueryRepository)
  // ============================================================================

  /**
   * Find all clients for a user with pagination and filtering
   */
  async findAll(
    userId: string,
    options?: QueryOptions & PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Client>>> {
    return clientQueryRepository.findAll(userId, options);
  }

  /**
   * Find a single client by ID
   */
  async findById(id: string): Promise<RepositoryResult<Client>> {
    return clientQueryRepository.findById(id);
  }

  /**
   * Find clients by user ID (alias for findAll with default options)
   */
  async findByUserId(userId: string): Promise<RepositoryResult<Client[]>> {
    return clientQueryRepository.findByUserId(userId);
  }

  /**
   * Search clients by name, email, or phone
   */
  async search(
    userId: string,
    query: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Client>>> {
    return clientQueryRepository.search(userId, query, options);
  }

  /**
   * Get total count of clients for a user
   */
  async count(userId: string): Promise<RepositoryResult<number>> {
    return clientQueryRepository.count(userId);
  }

  // ============================================================================
  // COMMAND OPERATIONS (delegated to ClientCommandRepository)
  // ============================================================================

  /**
   * Create a new client
   */
  async create(userId: string, clientData: Omit<Client, 'id' | 'userId'>): Promise<RepositoryResult<Client>> {
    return clientCommandRepository.create(userId, clientData);
  }

  /**
   * Update an existing client
   */
  async update(id: string, clientData: Partial<Client>): Promise<RepositoryResult<Client>> {
    return clientCommandRepository.update(id, clientData);
  }

  /**
   * Soft delete a client (sets is_deleted flag)
   */
  async delete(id: string): Promise<RepositoryResult<void>> {
    return clientCommandRepository.delete(id);
  }

  /**
   * Permanently delete a client (hard delete)
   * Use with caution - this cannot be undone
   */
  async hardDelete(id: string): Promise<RepositoryResult<void>> {
    return clientCommandRepository.hardDelete(id);
  }

  /**
   * Restore a soft-deleted client
   */
  async restore(id: string): Promise<RepositoryResult<Client>> {
    return clientCommandRepository.restore(id);
  }

  // ============================================================================
  // WORKFLOW OPERATIONS (delegated to ClientWorkflowService)
  // ============================================================================

  /**
   * Check if a client has dependencies (jobs, quotes, invoices)
   * Useful for preventing deletion of clients that are referenced elsewhere
   */
  async hasDependencies(id: string): Promise<RepositoryResult<ClientDependencies>> {
    return clientWorkflowService.hasDependencies(id);
  }

  // ============================================================================
  // RE-EXPORT SPECIALIZED MODULES FOR DIRECT USE
  // ============================================================================

  /**
   * Client Query Repository
   * Use this for read-only operations on clients
   */
  static Query = clientQueryRepository;

  /**
   * Client Command Repository
   * Use this for write operations on clients
   */
  static Command = clientCommandRepository;

  /**
   * Client Workflow Service
   * Use this for business logic and workflow operations
   */
  static Workflow = clientWorkflowService;
}

// Export singleton instance for backward compatibility
export const clientRepository = new ClientRepository();

// ============================================================================
// RE-EXPORT SPECIALIZED MODULES FOR DIRECT USE
// ============================================================================

/**
 * Client Query Repository
 * Use this for read-only operations on clients
 */
export { clientQueryRepository };

/**
 * Client Command Repository
 * Use this for write operations on clients
 */
export { clientCommandRepository };

/**
 * Client Workflow Service
 * Use this for business logic and workflow operations
 */
export {
  clientWorkflowService,
  ClientDependencies,
};

// Import types from ClientWorkflowService for re-export
import type { ClientDependencies } from './ClientWorkflowService';
